import React, { useEffect, useState, useCallback } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import backend from "../../backend";
import useTitle from "../../hooks/useTitle";
import useIsMobile from "../../hooks/useIsMobile";
import usePulidoLink from "../../hooks/usePulidoLink";
import ExpandableTable from "../../components/design-system/ExpandableTable";
import { formatRut } from "../../utils/rut";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtM = n => n > 0 ? "$" + Math.round(n).toLocaleString("es-CL") : "—";
const fmtD = d => d ? new Date(d).toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "2-digit" }) : "—";

const TYPE_META = {
  client:   { label: "Cliente",    bg: "#0d1a2d", color: "#3498db", border: "#0d2a4a" },
  provider: { label: "Proveedor",  bg: "#0d2a15", color: "#2ecc71", border: "#0d4020" },
  both:     { label: "Cli / Prov", bg: "#1a0d2a", color: "#9b59b6", border: "#2e0d4a" },
};

const TypeBadge = ({ type }) => {
  const m = TYPE_META[type] || { label: "—", bg: "#222", color: "#aaa", border: "#3a3a3a" };
  return (
    <span style={{
      display: "inline-block", fontSize: 10, fontWeight: 700,
      padding: "2px 7px", borderRadius: 20,
      background: m.bg, color: m.color, border: `1px solid ${m.border}`,
    }}>{m.label}</span>
  );
};

const FieldGroup = ({ children }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
    {children}
  </div>
);

const ViewField = ({ label, value, mono }) => (
  <div style={{ minWidth: 140 }}>
    <div style={{ fontSize: 10, color: "#aaa", marginBottom: 3, textTransform: "uppercase", letterSpacing: ".04em" }}>
      {label}
    </div>
    <div style={{ fontSize: 12, color: mono ? "#aaa" : "#ddd", fontFamily: mono ? "monospace" : undefined }}>
      {value || <span style={{ color: "#999" }}>—</span>}
    </div>
  </div>
);

const EditField = ({ label, children, width = 180 }) => (
  // .ent-field: en móvil el campo ocupa el ancho completo (CSS de index.css)
  <div className="ent-field" style={{ width }}>
    <label style={{ display: "block", fontSize: 10, color: "#aaa", marginBottom: 4, textTransform: "uppercase", letterSpacing: ".04em" }}>
      {label}
    </label>
    {children}
  </div>
);

const inputSt = {
  background: "#161616", border: "1px solid #333", borderRadius: 4,
  padding: "5px 8px", fontSize: 12, color: "#ddd", width: "100%", boxSizing: "border-box",
};

// ─── Panel de detalle (lazy stats) ────────────────────────────────────────────
const DetailPanel = ({ data, helpers, statsCache, onLoadStats, onDelete }) => {
  useEffect(() => {
    if (statsCache[data._id] === undefined) onLoadStats(data._id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data._id]);

  const stats = statsCache[data._id];
  const isClient   = data.type === "client"   || data.type === "both";
  const isProvider = data.type === "provider" || data.type === "both";

  if (helpers.isEditing) {
    const d = data;
    const u = updater => helpers.updateDraft(updater);
    const set = (field, val) => u(prev => ({ ...prev, [field]: val }));

    return (
      <div className="ent-detalle" style={{ padding: "16px 20px" }}>
        <FieldGroup>
          <EditField label="Nombre *" width={240}>
            <input style={inputSt} name="name" value={d.name || ""} onChange={e => set("name", e.target.value)} />
          </EditField>
          <EditField label="Tipo" width={150}>
            <select style={inputSt} name="type" value={d.type || "client"} onChange={e => set("type", e.target.value)}>
              <option value="client">Cliente</option>
              <option value="provider">Proveedor</option>
              <option value="both">Ambos</option>
            </select>
          </EditField>
          <EditField label="RUT" width={150}>
            <input
              style={inputSt}
              name="rut"
              value={d.rut || ""}
              onChange={e => set("rut", e.target.value)}
              onBlur={e => {
                const v = e.target.value;
                if (!v) return;
                const f = formatRut(v);
                if (f !== v) set("rut", f);
              }}
              placeholder="12.345.678-9"
            />
          </EditField>
          <EditField label="Estado" width={130}>
            <select style={inputSt} name="status" value={d.status || "active"} onChange={e => set("status", e.target.value)}>
              <option value="active">Activo</option>
              <option value="archived">Archivado</option>
            </select>
          </EditField>
          {/* Fase F (26-ago): vencimiento de las facturas de compra de este
              proveedor = fecha + estos días. Lo lee el tab Pagos y el aviso matinal. */}
          {(d.type === "provider" || d.type === "both") && (
            <EditField label="Días de pago (proveedor)" width={170}>
              <input
                style={inputSt} type="number" min={0} max={365}
                name="paymentTermsDays"
                value={d.paymentTermsDays ?? 30}
                onChange={e => set("paymentTermsDays", Number(e.target.value))}
              />
            </EditField>
          )}
        </FieldGroup>

        <div style={{ height: 12 }} />

        <FieldGroup>
          <EditField label="Email" width={220}>
            <input style={inputSt} name="email" type="email" value={d.email || ""} onChange={e => set("email", e.target.value)} />
          </EditField>
          <EditField label="Teléfono" width={150}>
            <input style={inputSt} name="phone" value={d.phone || ""} onChange={e => set("phone", e.target.value)} />
          </EditField>
          <EditField label="Persona de contacto" width={200}>
            <input style={inputSt} name="contactPerson" value={d.contactPerson || ""} onChange={e => set("contactPerson", e.target.value)} />
          </EditField>
          <EditField label="Cargo" width={170}>
            <input style={inputSt} name="contactRole" value={d.contactRole || ""} onChange={e => set("contactRole", e.target.value)} />
          </EditField>
        </FieldGroup>

        <div style={{ height: 12 }} />

        <FieldGroup>
          <EditField label="Dirección" width={280}>
            <input style={inputSt} name="address" value={d.address || ""} onChange={e => set("address", e.target.value)} />
          </EditField>
          <EditField label="Comuna" width={150}>
            <input style={inputSt} name="comuna" value={d.comuna || ""} onChange={e => set("comuna", e.target.value)} placeholder="va en el DTE" />
          </EditField>
          <EditField label="Ciudad" width={150}>
            <input style={inputSt} name="city" value={d.city || ""} onChange={e => set("city", e.target.value)} />
          </EditField>
          <EditField label="Giro / Actividad" width={220}>
            <input style={inputSt} name="giro" value={d.giro || ""} onChange={e => set("giro", e.target.value)} />
          </EditField>
        </FieldGroup>

        <div style={{ height: 12 }} />

        <EditField label="Notas internas" width="100%">
          <textarea
            style={{ ...inputSt, minHeight: 72, resize: "vertical" }}
            name="notes"
            value={d.notes || ""}
            onChange={e => set("notes", e.target.value)}
          />
        </EditField>

        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button
            onClick={helpers.saveEdit}
            disabled={helpers.isSaving}
            style={{
              background: "#e30613", color: "#fff", border: "none",
              borderRadius: 5, padding: "6px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer",
            }}
          >
            {helpers.isSaving ? "Guardando…" : "Guardar"}
          </button>
          <button
            onClick={helpers.cancelEdit}
            style={{
              background: "transparent", border: "1px solid #333", color: "#cbd5e1",
              borderRadius: 5, padding: "6px 14px", fontSize: 12, cursor: "pointer",
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ent-detalle" style={{ padding: "16px 20px" }}>
      {/* Datos de contacto */}
      <FieldGroup>
        <ViewField label="Email"            value={data.email}         />
        <ViewField label="Teléfono"         value={data.phone}         />
        <ViewField label="Persona contacto" value={data.contactPerson} />
        <ViewField label="Cargo"            value={data.contactRole}   />
        <ViewField label="Dirección"        value={data.address}       />
        <ViewField label="Comuna"           value={data.comuna}        />
        <ViewField label="Ciudad"           value={data.city}          />
        <ViewField label="Giro"             value={data.giro}          />
        <ViewField label="RUT"              value={data.rut}    mono   />
        <ViewField label="Estado"           value={data.status === "archived" ? "Archivado" : "Activo"} />
      </FieldGroup>

      {data.notes && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 10, color: "#aaa", marginBottom: 4, textTransform: "uppercase", letterSpacing: ".04em" }}>
            Notas internas
          </div>
          <div style={{
            fontSize: 12, color: "#bbb", background: "#1a1a1a",
            border: "1px solid #2e2e2e", borderRadius: 5, padding: "8px 10px",
            whiteSpace: "pre-wrap", maxWidth: 600,
          }}>
            {data.notes}
          </div>
        </div>
      )}

      {/* Estadísticas */}
      <div style={{ marginTop: 16, borderTop: "1px solid #2e2e2e", paddingTop: 14 }}>
        <div style={{ fontSize: 10, color: "#999", marginBottom: 10, textTransform: "uppercase", letterSpacing: ".05em" }}>
          Estadísticas
        </div>
        {stats === undefined || stats === null ? (
          <div style={{ fontSize: 12, color: "#999" }}>
            {stats === null ? "Cargando…" : "—"}
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            {isClient && (
              <>
                <StatCard
                  label="OTs totales"
                  value={stats.asClient?.workOrdersCount ?? 0}
                  color="#3498db"
                />
                <StatCard
                  label="Total facturado"
                  value={fmtM(stats.asClient?.totalRevenue || 0)}
                  color="#2ecc71"
                />
                {stats.asClient?.lastWorkOrder && (
                  <div style={{ minWidth: 180 }}>
                    <div style={{ fontSize: 10, color: "#aaa", marginBottom: 3, textTransform: "uppercase", letterSpacing: ".04em" }}>
                      Última OT
                    </div>
                    <div style={{ fontSize: 12, color: "#ddd" }}>
                      N° {stats.asClient.lastWorkOrder.number}
                      {" · "}
                      <span style={{ color: "#cbd5e1" }}>{fmtD(stats.asClient.lastWorkOrder.createdAt)}</span>
                    </div>
                    {stats.asClient.lastWorkOrder.name && (
                      <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>{stats.asClient.lastWorkOrder.name}</div>
                    )}
                  </div>
                )}
              </>
            )}
            {isProvider && (
              <>
                <StatCard
                  label="Compras"
                  value={stats.asProvider?.purchasesCount ?? 0}
                  color="#f39c12"
                />
                <StatCard
                  label="Total gastado"
                  value={fmtM(stats.asProvider?.totalSpent || 0)}
                  color="#e74c3c"
                />
              </>
            )}
          </div>
        )}
      </div>

      {/* Acciones */}
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button
          onClick={helpers.startEdit}
          style={{
            background: "#2d2000", color: "#f39c12", border: "1px solid #4a3500",
            borderRadius: 5, padding: "5px 14px", fontSize: 12, cursor: "pointer",
          }}
        >
          ✎ Editar
        </button>
        <button
          onClick={() => onDelete(data._id)}
          style={{
            background: "transparent", color: "#999", border: "1px solid #2e2e2e",
            borderRadius: 5, padding: "5px 14px", fontSize: 12, cursor: "pointer",
          }}
        >
          Archivar
        </button>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div style={{ minWidth: 120 }}>
    <div style={{ fontSize: 10, color: "#aaa", marginBottom: 3, textTransform: "uppercase", letterSpacing: ".04em" }}>
      {label}
    </div>
    <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
  </div>
);

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
const Entities = () => {
  useTitle("Contactos");
  const navigate  = useNavigate();
  const location  = useLocation();
  // Rama móvil (26-ago): buscador y botón a lo ancho; la tabla ya se vuelve
  // tarjetas sola (ExpandableTable trae su propio modo móvil).
  const isMobile  = useIsMobile();

  const [entities,   setEntities]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState("");
  const [tab,        setTab]        = useState("");
  const [statsCache, setStatsCache] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try { setEntities((await backend.get("entity")).data || []); }
    catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle entity created via child route "new"
  useEffect(() => {
    if (location.state?.new) {
      setEntities(p => [...p, location.state.new]);
      navigate("", { replace: true });
    }
    if (location.state?.edit) {
      const e = location.state.edit;
      setEntities(p => p.map(v => v._id === e._id ? e : v));
      navigate("", { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const loadStats = useCallback(async entityId => {
    setStatsCache(prev => ({ ...prev, [entityId]: null }));
    try {
      const res = await backend.get(`entity/${entityId}/stats`);
      setStatsCache(prev => ({ ...prev, [entityId]: res.data }));
    } catch {
      setStatsCache(prev => ({ ...prev, [entityId]: {} }));
    }
  }, []);

  const handleSave = useCallback(async (id, draft) => {
    await backend.patch("entity", { _id: id, ...draft });
    setEntities(prev => prev.map(e => e._id === id ? { ...e, ...draft } : e));
  }, []);

  const handleArchive = useCallback(async (id) => {
    if (!window.confirm("¿Archivar este contacto?")) return;
    await backend.patch("entity/archive", { _id: id });
    setEntities(prev => prev.map(e => e._id === id ? { ...e, status: "archived" } : e));
  }, []);

  // Derived data
  const filtered = entities.filter(v => {
    const q = filter.toLowerCase();
    const matchText = !q ||
      (v.name          || "").toLowerCase().includes(q) ||
      (v.email         || "").toLowerCase().includes(q) ||
      (v.rut           || "").toLowerCase().includes(q) ||
      (v.contactPerson || "").toLowerCase().includes(q);
    const matchTab = !tab || tab === v.type || (tab === "client" && v.type === "both") || (tab === "provider" && v.type === "both");
    return matchText && matchTab;
  });

  // D-T51b — Link directo desde /pulido (?openId=<entityId>&editField=rut o ?compare=<id1>,<id2>)
  const pulidoLink = usePulidoLink({ rows: entities });

  useEffect(() => {
    if (!pulidoLink.openId || !entities.length) return;
    const inFiltered = filtered.some(e => e._id === pulidoLink.openId);
    if (!inFiltered) {
      setFilter("");
      setTab("");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulidoLink.openId, entities]);

  const clientes    = entities.filter(v => v.type === "client" || v.type === "both").length;
  const proveedores = entities.filter(v => v.type === "provider" || v.type === "both").length;

  const columns = [
    {
      key: "type", label: "Tipo", width: 95,
      render: v => <TypeBadge type={v} />,
    },
    {
      key: "name", label: "Nombre", width: 260,
      render: v => <span style={{ fontWeight: 600, color: "#fff" }}>{v || "—"}</span>,
    },
    {
      key: "rut", label: "RUT", width: 130,
      render: v => <span style={{ fontFamily: "monospace", color: "#bbb" }}>{v || "—"}</span>,
    },
    {
      key: "email", label: "Email",
      render: v => v || <span style={{ color: "#999" }}>—</span>,
    },
    {
      key: "phone", label: "Teléfono", width: 140,
      render: v => v || <span style={{ color: "#999" }}>—</span>,
    },
  ];

  return (
    <div style={{
      display: "block", padding: "clamp(12px, 2.5vw, 18px) clamp(10px, 3vw, 22px)", background: "#1a1a1a",
      minHeight: "100%", color: "#ddd", fontFamily: "Arial,sans-serif",
      fontSize: 13, boxSizing: "border-box", width: "100%",
    }}>

      {/* ── Header ── (móvil: título 17/800, buscador y botón a lo ancho) */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: isMobile ? 12 : 18, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: isMobile ? 17 : 16, fontWeight: isMobile ? 800 : 700, color: "#fff" }}>Contactos</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", flex: isMobile ? "1 1 100%" : undefined }}>
          <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Buscar por nombre, RUT, email o contacto…"
            style={{
              background: "#161616", border: "1px solid #333", borderRadius: 5,
              padding: "7px 10px", fontSize: 12, color: "#ddd",
              width: isMobile ? "100%" : 280, boxSizing: "border-box",
              flex: isMobile ? "1 1 100%" : undefined,
            }}
          />
          <button
            onClick={() => navigate("new", { state: { back: true } })}
            className="t-02"
            style={{
              background: "#e30613", color: "#fff", border: "none",
              borderRadius: isMobile ? 10 : 6, padding: "7px 16px", fontSize: 12,
              fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
              flex: isMobile ? "1 1 100%" : undefined,
            }}
          >
            + Agregar contacto
          </button>
        </div>
      </div>

      {/* ── Métricas ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        {[
          { label: "Total",       value: entities.length, color: "#ddd"    },
          { label: "Clientes",    value: clientes,        color: "#3498db" },
          { label: "Proveedores", value: proveedores,     color: "#2ecc71" },
        ].map(m => (
          <div key={m.label} style={{
            flex: "1 1 0", background: "#242424", border: "1px solid #2e2e2e",
            borderRadius: isMobile ? 12 : 8, padding: "10px 14px",
          }}>
            <div style={{ fontSize: 10, color: "#aaa", marginBottom: 3, textTransform: "uppercase", letterSpacing: ".05em" }}>
              {m.label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: m.color, fontVariantNumeric: isMobile ? "tabular-nums" : undefined }}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: "flex", borderBottom: "1px solid #2e2e2e", marginBottom: 14 }}>
        {[["", "Todos"], ["client", "Clientes"], ["provider", "Proveedores"]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: "7px 16px", background: "transparent", border: "none",
            borderBottom: tab === key ? "2px solid #e30613" : "2px solid transparent",
            color: tab === key ? "#fff" : "#555",
            fontSize: 12, fontWeight: tab === key ? 700 : 400, cursor: "pointer",
          }}>{label}</button>
        ))}
      </div>

      {/* ── Tabla ── */}
      <div className="bini-table-wrap">
        {loading ? (
          <div style={{ padding: "32px", textAlign: "center", color: "#999", fontSize: 12 }}>
            Cargando…
          </div>
        ) : (
          <ExpandableTable
            rows={filtered}
            columns={columns}
            emptyText={filter ? "Sin resultados" : "No hay contactos registrados"}
            onSave={handleSave}
            initialExpandedId={pulidoLink.openId}
            initialEditingId={pulidoLink.editingId}
            highlightIds={pulidoLink.compareIds}
            renderDetail={(data, helpers) => (
              <DetailPanel
                data={data}
                helpers={helpers}
                statsCache={statsCache}
                onLoadStats={loadStats}
                onDelete={handleArchive}
              />
            )}
          />
        )}
      </div>

      <Outlet />
    </div>
  );
};

export default Entities;
